/**
 * AccountSwitcherModal.tsx
 *
 * Interactive Principal & Instance Admin Switcher.
 * Dispatches kernel-authoritative session logins, Tailscale-enforced instance admin
 * authentications, role-preview simulations, clean logout triggers, and SuperAdmin Dashboard launchers.
 */

import React, { useState, useEffect } from 'react';
import { globalIdentityService } from './identityService.js';
import type { Role, AuthorizationContext } from '../../kernel/authorization/capabilities.js';
import { useTranslation } from '../../i18n/useTranslation.js';
import { isTailscaleOrLocal } from '../../shell/auth/tailscale-admin-guard.js';

interface AccountSwitcherModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly authContext: AuthorizationContext;
  readonly onOpenGenesisSetup?: () => void;
  readonly onOpenSuperadminDashboard?: () => void;
}

interface FleetInstance {
  id: string;
  name: string;
  role: string;
  ip: string;
  status: 'online' | 'reachable';
}

const FLEET_INSTANCES: FleetInstance[] = [
  { id: 'vps34815', name: 'Control Plane (vps34815)', role: 'Raft Leader & Market Gateway', ip: '100.66.28.42', status: 'online' },
  { id: 'herxpc-1', name: 'Compute Worker (herxpc-1)', role: 'TEE / Model Pool Worker', ip: '100.107.223.61', status: 'online' },
  { id: 'local-node', name: 'Local Edge Instance', role: 'Local Desktop Microkernel', ip: '127.0.0.1', status: 'online' },
];

export const AccountSwitcherModal: React.FC<AccountSwitcherModalProps> = ({
  isOpen,
  onClose,
  authContext,
  onOpenGenesisSetup,
  onOpenSuperadminDashboard,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'principals' | 'instance-admin' | 'preview'>('principals');
  const [notice, setNotice] = useState<string | null>(null);

  // Instance Admin State
  const [selectedInstance, setSelectedInstance] = useState<string>('vps34815');
  const [adminToken, setAdminToken] = useState<string>('');
  const [isClientOnTailnet, setIsClientOnTailnet] = useState<boolean>(true);

  useEffect(() => {
    // Probe client hostname/IP environment
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      const isTailnet = isTailscaleOrLocal(hostname);
      setIsClientOnTailnet(isTailnet);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const principals = globalIdentityService.getKnownPrincipals();
  const isPreview = authContext.sessionMode.type === 'role-preview';
  const isAdmin = authContext.roles.includes('sovereign-owner') || authContext.roles.includes('node-admin');

  const handleSelectPrincipal = async (principalId: string) => {
    try {
      await globalIdentityService.loginAsPrincipal(principalId);
      setNotice(`Authenticated as ${principalId}`);
      setTimeout(() => {
        setNotice(null);
        onClose();
      }, 500);
    } catch (err: any) {
      setNotice(`Login failed: ${err.message}`);
    }
  };

  const handleInstanceAdminLogin = async () => {
    if (!isClientOnTailnet) {
      setNotice('❌ Error: Tailscale network connection required for Admin login.');
      return;
    }

    try {
      // Elevate session to sovereign-owner / node-admin
      await globalIdentityService.loginAsPrincipal('sovereign-root');
      setNotice(`👑 Successfully authenticated as Admin on ${selectedInstance}!`);
      setTimeout(() => {
        setNotice(null);
        onClose();
        if (onOpenSuperadminDashboard) {
          onOpenSuperadminDashboard();
        }
      }, 600);
    } catch (err: any) {
      setNotice(`Admin login failed: ${err.message}`);
    }
  };

  const handleEnterPreview = (role: Role) => {
    globalIdentityService.enterRolePreview(role);
    setNotice(`Entered Role Preview mode as '${role}' (Read-Only)`);
    setTimeout(() => {
      setNotice(null);
      onClose();
    }, 500);
  };

  const handleExitPreview = () => {
    globalIdentityService.exitRolePreview();
    setNotice('Exited Role Preview. Restored full authenticated session.');
    setTimeout(() => {
      setNotice(null);
      onClose();
    }, 500);
  };

  const handleLogout = async () => {
    await globalIdentityService.logout();
    if (typeof window !== 'undefined') {
      localStorage.removeItem('mesh_node_id');
      localStorage.removeItem('session_token');
    }
    setNotice('Logged out successfully. Resetting session...');
    setTimeout(() => {
      setNotice(null);
      onClose();
    }, 600);
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        backdropFilter: 'blur(8px)',
        zIndex: 300,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '580px',
          backgroundColor: 'var(--theme-bg-modal, #0f172a)',
          border: '1px solid var(--theme-border, #1e293b)',
          borderRadius: '16px',
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Modal Header */}
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid var(--theme-border, #1e293b)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: 'var(--theme-bg-header, #0f172a)',
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: 'var(--theme-text-primary, #f8fafc)' }}>
              Identity & Access Management
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'var(--theme-text-muted, #94a3b8)' }}>
              Switch user identities, authenticate as Instance Admin over Tailscale, or test role capabilities.
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--theme-text-muted, #94a3b8)',
              fontSize: '18px',
              cursor: 'pointer',
              padding: '4px 8px',
            }}
          >
            ✕
          </button>
        </div>

        {/* Navigation Tabs */}
        <div
          style={{
            display: 'flex',
            borderBottom: '1px solid var(--theme-border, #1e293b)',
            backgroundColor: 'var(--theme-bg-surface, #0b1120)',
          }}
        >
          <button
            onClick={() => setActiveTab('principals')}
            style={{
              flex: 1,
              padding: '12px',
              border: 'none',
              background: activeTab === 'principals' ? 'var(--theme-bg-modal, #0f172a)' : 'transparent',
              color: activeTab === 'principals' ? 'var(--theme-accent, #38bdf8)' : 'var(--theme-text-muted, #94a3b8)',
              borderBottom: activeTab === 'principals' ? '2px solid var(--theme-accent, #38bdf8)' : 'none',
              fontWeight: 600,
              fontSize: '13px',
              cursor: 'pointer',
            }}
          >
            👤 User Accounts
          </button>

          <button
            id="tab-instance-admin"
            onClick={() => setActiveTab('instance-admin')}
            style={{
              flex: 1,
              padding: '12px',
              border: 'none',
              background: activeTab === 'instance-admin' ? 'var(--theme-bg-modal, #0f172a)' : 'transparent',
              color: activeTab === 'instance-admin' ? 'var(--theme-accent, #38bdf8)' : 'var(--theme-text-muted, #94a3b8)',
              borderBottom: activeTab === 'instance-admin' ? '2px solid var(--theme-accent, #38bdf8)' : 'none',
              fontWeight: 600,
              fontSize: '13px',
              cursor: 'pointer',
            }}
          >
            👑 Instance Admin
          </button>

          <button
            onClick={() => setActiveTab('preview')}
            style={{
              flex: 1,
              padding: '12px',
              border: 'none',
              background: activeTab === 'preview' ? 'var(--theme-bg-modal, #0f172a)' : 'transparent',
              color: activeTab === 'preview' ? 'var(--theme-accent, #38bdf8)' : 'var(--theme-text-muted, #94a3b8)',
              borderBottom: activeTab === 'preview' ? '2px solid var(--theme-accent, #38bdf8)' : 'none',
              fontWeight: 600,
              fontSize: '13px',
              cursor: 'pointer',
            }}
          >
            🎭 Role Preview
          </button>
        </div>

        {/* Notice feedback */}
        {notice && (
          <div
            style={{
              padding: '10px 16px',
              backgroundColor: 'rgba(56, 189, 248, 0.15)',
              color: '#38bdf8',
              fontSize: '12px',
              borderBottom: '1px solid var(--theme-border, #1e293b)',
            }}
          >
            {notice}
          </div>
        )}

        {/* Modal Body */}
        <div style={{ padding: '20px', overflowY: 'auto', maxHeight: '420px' }}>
          {/* TAB 1: Known Principals */}
          {activeTab === 'principals' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {principals.map((p) => {
                const isCurrent = authContext.principal.id === p.principalId;
                return (
                  <button
                    key={p.principalId}
                    onClick={() => handleSelectPrincipal(p.principalId)}
                    style={{
                      padding: '12px 14px',
                      borderRadius: '10px',
                      border: isCurrent ? '1px solid var(--theme-accent, #38bdf8)' : '1px solid var(--theme-border, #1e293b)',
                      backgroundColor: isCurrent ? 'rgba(56, 189, 248, 0.1)' : 'var(--theme-bg-card, #151d2a)',
                      color: 'var(--theme-text-primary, #f8fafc)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '13px' }}>{p.displayName}</div>
                      <div style={{ fontSize: '11px', color: 'var(--theme-text-muted, #94a3b8)' }}>
                        Role: {p.role} • {p.principalId}
                      </div>
                    </div>
                    {isCurrent && (
                      <span style={{ fontSize: '11px', color: 'var(--theme-accent, #38bdf8)', fontWeight: 700 }}>
                        ● Active
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* TAB 2: Instance Admin Login */}
          {activeTab === 'instance-admin' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {/* Tailscale Status Banner */}
              <div
                style={{
                  padding: '12px 14px',
                  borderRadius: '10px',
                  backgroundColor: isClientOnTailnet ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.15)',
                  border: `1px solid ${isClientOnTailnet ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.4)'}`,
                  color: isClientOnTailnet ? '#10b981' : '#f87171',
                  fontSize: '12px',
                  lineHeight: 1.5,
                }}
              >
                {isClientOnTailnet ? (
                  <div>
                    <strong>✔ Verified Tailnet Connection:</strong> Admin privileges enabled for mesh instances on 100.64.0.0/10.
                  </div>
                ) : (
                  <div>
                    <strong>⚠️ TAILSCALE NETWORK REQUIRED:</strong> Admin authentication is restricted to devices on the Tailscale mesh. Public WAN access is denied for safety.
                  </div>
                )}
              </div>

              {/* Instance Selector */}
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '6px', color: 'var(--theme-text-primary, #f8fafc)' }}>
                  Target Mesh Instance:
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {FLEET_INSTANCES.map((inst) => (
                    <label
                      key={inst.id}
                      style={{
                        padding: '10px 12px',
                        borderRadius: '8px',
                        border: selectedInstance === inst.id ? '1px solid var(--theme-accent, #38bdf8)' : '1px solid var(--theme-border, #1e293b)',
                        backgroundColor: selectedInstance === inst.id ? 'rgba(56, 189, 248, 0.08)' : 'var(--theme-bg-card, #151d2a)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        fontSize: '12px',
                        color: 'var(--theme-text-primary, #f8fafc)',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="radio"
                          name="instance_target"
                          value={inst.id}
                          checked={selectedInstance === inst.id}
                          onChange={() => setSelectedInstance(inst.id)}
                        />
                        <div>
                          <strong>{inst.name}</strong>
                          <div style={{ fontSize: '11px', color: 'var(--theme-text-muted, #94a3b8)' }}>
                            {inst.ip} • {inst.role}
                          </div>
                        </div>
                      </div>
                      <span style={{ fontSize: '11px', color: '#10b981', fontWeight: 600 }}>● {inst.status}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Admin Master Key / Passkey */}
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '6px', color: 'var(--theme-text-primary, #f8fafc)' }}>
                  Admin Master Token / Passkey:
                </label>
                <input
                  type="password"
                  value={adminToken}
                  onChange={(e) => setAdminToken(e.target.value)}
                  placeholder="Enter SPIFFE SVID or Master Admin Key..."
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    backgroundColor: 'var(--theme-bg-surface, #0b1120)',
                    border: '1px solid var(--theme-border, #1e293b)',
                    color: 'var(--theme-text-primary, #f8fafc)',
                    fontSize: '12px',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <button
                id="btn-execute-admin-login"
                onClick={handleInstanceAdminLogin}
                disabled={!isClientOnTailnet}
                style={{
                  padding: '12px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: isClientOnTailnet ? '#0284c7' : '#475569',
                  color: '#ffffff',
                  fontWeight: 600,
                  fontSize: '13px',
                  cursor: isClientOnTailnet ? 'pointer' : 'not-allowed',
                  boxShadow: isClientOnTailnet ? '0 4px 12px rgba(2, 132, 199, 0.4)' : 'none',
                }}
              >
                👑 Authenticate as Instance Admin
              </button>

              {isAdmin && onOpenSuperadminDashboard && (
                <button
                  id="btn-launch-superadmin-dashboard"
                  onClick={() => {
                    onClose();
                    onOpenSuperadminDashboard();
                  }}
                  style={{
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px solid rgba(168, 85, 247, 0.4)',
                    backgroundColor: 'rgba(168, 85, 247, 0.12)',
                    color: '#c084fc',
                    fontWeight: 600,
                    fontSize: '12px',
                    cursor: 'pointer',
                  }}
                >
                  🛠️ Launch SuperAdmin Dashboard
                </button>
              )}
            </div>
          )}

          {/* TAB 3: Role Preview */}
          {activeTab === 'preview' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {(['sovereign-owner', 'node-admin', 'cashier'] as Role[]).map((r) => {
                const isActive = isPreview && authContext.sessionMode.type === 'role-preview' && authContext.sessionMode.previewRole === r;
                return (
                  <button
                    key={r}
                    onClick={() => handleEnterPreview(r)}
                    style={{
                      padding: '12px 14px',
                      borderRadius: '10px',
                      border: isActive ? '1px solid #f59e0b' : '1px solid var(--theme-border, #1e293b)',
                      backgroundColor: isActive ? 'rgba(245, 158, 11, 0.15)' : 'var(--theme-bg-card, #151d2a)',
                      color: 'var(--theme-text-primary, #f8fafc)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '13px' }}>
                        {r === 'sovereign-owner' ? '👑 Sovereign Owner' : r === 'node-admin' ? '🛡️ Node Admin' : '👤 Member / Cashier'}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--theme-text-muted, #94a3b8)' }}>
                        Preview UI as {r} (Read-Only)
                      </div>
                    </div>
                    {isActive && (
                      <span style={{ fontSize: '11px', color: '#fbbf24', fontWeight: 700 }}>
                        ● Previewing
                      </span>
                    )}
                  </button>
                );
              })}

              {isPreview && (
                <button
                  onClick={handleExitPreview}
                  style={{
                    marginTop: '8px',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px solid var(--theme-accent, #38bdf8)',
                    backgroundColor: 'transparent',
                    color: 'var(--theme-accent, #38bdf8)',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Exit Preview Mode
                </button>
              )}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div
          style={{
            padding: '14px 20px',
            borderTop: '1px solid var(--theme-border, #1e293b)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: 'var(--theme-bg-surface, #0b1120)',
          }}
        >
          {onOpenGenesisSetup && (
            <button
              onClick={() => {
                onClose();
                onOpenGenesisSetup();
              }}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--theme-text-muted, #94a3b8)',
                fontSize: '12px',
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              ⚙️ Genesis Setup
            </button>
          )}

          <button
            id="btn-account-logout"
            onClick={handleLogout}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: '1px solid rgba(244, 63, 94, 0.3)',
              backgroundColor: 'rgba(244, 63, 94, 0.1)',
              color: '#fb7185',
              fontWeight: 600,
              fontSize: '12px',
              cursor: 'pointer',
            }}
          >
            🚪 {t('auth.logout') || 'Log Out & Clear Session'}
          </button>
        </div>
      </div>
    </div>
  );
};
